package com.writerslab.office

// Driven by PREVIEW_MODE env var injected at container start via envsubst.
// For local Gradle dev server set ?preview=true in the URL or export PREVIEW_MODE=true before docker run.
internal val PREVIEW: Boolean = js("window._officePreview") == "true"

// LOCAL-ONLY: skip the Keycloak login and send API requests without a Bearer
// token. Set for localhost in index.html so the office can be exercised
// against a backend running with the dev-no-auth profile. Off in prod.
internal val NO_AUTH: Boolean = js("window._officeNoAuth") == "true"

internal object MockApi {
    fun stats() = OfficeStats(
        totalUsers = 142,
        totalBooks = 381,
        totalUserBooks = 524,
        runningJobs = 3,
        doneJobs = 289,
        failedJobs = 12,
        pipelinePaidDone = 241,
        pipelineFreeDone = 48,
        totalCreditsInCirculation = 4830,
        totalCodes = 50,
        availableCodes = 34,
        redeemedCodes = 16,
        totalFreeAnnotations = 87,
        totalPaidAnnotations = 34,
        totalFreeCovers = 53,
        totalPaidCoverBatches = 18,
        totalPaidCoverRegens = 42,
        totalFreeTeasers = 34,
        totalPaidTeasers = 11,
    )

    fun codes() = listOf(
        OfficeCode("WL-A7FK-3DMP-X2NQ", 20, "beta 2026-04", "office-admin-1", "2026-04-17T10:00:00Z", null, null),
        OfficeCode("WL-B8GK-4ENQ-Y3PR", 20, "beta 2026-04", "office-admin-1", "2026-04-17T10:00:00Z", "user-abc123", "2026-04-20T14:32:00Z"),
        OfficeCode("WL-C9HM-5FPR-Z4QS", 50, "press kit", "office-admin-2", "2026-05-01T09:00:00Z", null, null),
        OfficeCode("WL-D2JN-6GQS-A5RT", 50, "press kit", "office-admin-2", "2026-05-01T09:00:00Z", "user-xyz789", "2026-05-03T11:15:00Z"),
        OfficeCode("WL-E3KP-7HRT-B6SV", 20, null, "office-admin-1", "2026-06-01T08:00:00Z", null, null),
    )

    fun users() = listOf(
        // balance, paid-credits, code-credits, books,
        // pipeline-done, pipeline-charged,
        // free-annots, paid-annots, free-covers, paid-covers, paid-regen,
        // free-teasers, paid-teasers
        OfficeUser("user-abc123", "alice@example.com", 340, 500, 70, 3, 2, 180, 3, 1, 2, 1, 0, 2, 1, "2026-03-12T10:00:00Z"),
        OfficeUser("user-xyz789", "bob@example.com", 80, 200, 50, 5, 4, 170, 5, 2, 3, 2, 1, 3, 0, "2026-04-01T08:30:00Z"),
        OfficeUser("user-qrs456", "carol@example.com", 10, 0, 20, 1, 0, 0, 1, 0, 1, 0, 0, 0, 0, "2026-05-20T14:15:00Z"),
        OfficeUser("user-def111", "dan@example.com", 0, 100, 0, 2, 1, 90, 2, 1, 1, 1, 0, 1, 0, "2026-06-01T11:00:00Z"),
        OfficeUser("_anon", "anon-7f3a@temp.local", 5, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, "2026-06-27T08:10:00Z"),
    )

    fun analytics(period: String): List<OfficeAnalyticsBucket> = when (period) {
        "week" -> listOf(
            OfficeAnalyticsBucket("2026-04-28", registrations = 4, payments = 0, creditsViaPayment = 0, codeRedemptions = 1, books = 6),
            OfficeAnalyticsBucket("2026-05-05", registrations = 8, payments = 1, creditsViaPayment = 200, codeRedemptions = 2, books = 11),
            OfficeAnalyticsBucket("2026-05-12", registrations = 12, payments = 2, creditsViaPayment = 450, codeRedemptions = 1, books = 15),
            OfficeAnalyticsBucket("2026-05-19", registrations = 7, payments = 0, creditsViaPayment = 0, codeRedemptions = 0, books = 8),
            OfficeAnalyticsBucket("2026-05-26", registrations = 15, payments = 3, creditsViaPayment = 600, codeRedemptions = 3, books = 18),
            OfficeAnalyticsBucket("2026-06-02", registrations = 10, payments = 1, creditsViaPayment = 250, codeRedemptions = 2, books = 13),
            OfficeAnalyticsBucket("2026-06-09", registrations = 18, payments = 4, creditsViaPayment = 900, codeRedemptions = 4, books = 21),
            OfficeAnalyticsBucket("2026-06-16", registrations = 22, payments = 5, creditsViaPayment = 1100, codeRedemptions = 5, books = 27),
            OfficeAnalyticsBucket("2026-06-23", registrations = 14, payments = 3, creditsViaPayment = 700, codeRedemptions = 2, books = 18),
        )
        "month" -> listOf(
            OfficeAnalyticsBucket("2026-01", registrations = 12, payments = 1, creditsViaPayment = 200, codeRedemptions = 2, books = 15),
            OfficeAnalyticsBucket("2026-02", registrations = 17, payments = 2, creditsViaPayment = 450, codeRedemptions = 3, books = 22),
            OfficeAnalyticsBucket("2026-03", registrations = 25, payments = 3, creditsViaPayment = 600, codeRedemptions = 4, books = 33),
            OfficeAnalyticsBucket("2026-04", registrations = 31, payments = 5, creditsViaPayment = 1050, codeRedemptions = 6, books = 42),
            OfficeAnalyticsBucket("2026-05", registrations = 52, payments = 8, creditsViaPayment = 1800, codeRedemptions = 9, books = 70),
            OfficeAnalyticsBucket("2026-06", registrations = 110, payments = 15, creditsViaPayment = 3400, codeRedemptions = 16, books = 133),
        )
        else -> listOf(
            OfficeAnalyticsBucket("2026-05-28", registrations = 2, payments = 0, creditsViaPayment = 0, codeRedemptions = 0, books = 3, visitors = 99, newVisitors = 27, loggedInVisitors = 7),
            OfficeAnalyticsBucket("2026-05-29", registrations = 3, payments = 1, creditsViaPayment = 200, codeRedemptions = 1, books = 4, visitors = 140, newVisitors = 39, loggedInVisitors = 10),
            OfficeAnalyticsBucket("2026-05-30", registrations = 1, payments = 0, creditsViaPayment = 0, codeRedemptions = 0, books = 2, visitors = 58, newVisitors = 15, loggedInVisitors = 4),
            OfficeAnalyticsBucket("2026-05-31", registrations = 4, payments = 0, creditsViaPayment = 0, codeRedemptions = 1, books = 6, visitors = 181, newVisitors = 51, loggedInVisitors = 13),
            OfficeAnalyticsBucket("2026-06-01", registrations = 5, payments = 1, creditsViaPayment = 250, codeRedemptions = 0, books = 7, visitors = 222, newVisitors = 63, loggedInVisitors = 16),
            OfficeAnalyticsBucket("2026-06-02", registrations = 3, payments = 0, creditsViaPayment = 0, codeRedemptions = 1, books = 5, visitors = 140, newVisitors = 39, loggedInVisitors = 10),
            OfficeAnalyticsBucket("2026-06-03", registrations = 6, payments = 2, creditsViaPayment = 450, codeRedemptions = 2, books = 8, visitors = 263, newVisitors = 75, loggedInVisitors = 19),
            OfficeAnalyticsBucket("2026-06-04", registrations = 4, payments = 1, creditsViaPayment = 200, codeRedemptions = 0, books = 6, visitors = 181, newVisitors = 51, loggedInVisitors = 13),
            OfficeAnalyticsBucket("2026-06-05", registrations = 7, payments = 1, creditsViaPayment = 250, codeRedemptions = 1, books = 10, visitors = 304, newVisitors = 87, loggedInVisitors = 22),
            OfficeAnalyticsBucket("2026-06-10", registrations = 8, payments = 2, creditsViaPayment = 400, codeRedemptions = 2, books = 11, visitors = 345, newVisitors = 99, loggedInVisitors = 25),
            OfficeAnalyticsBucket("2026-06-15", registrations = 11, payments = 3, creditsViaPayment = 650, codeRedemptions = 3, books = 14, visitors = 468, newVisitors = 135, loggedInVisitors = 34),
            OfficeAnalyticsBucket("2026-06-20", registrations = 9, payments = 2, creditsViaPayment = 450, codeRedemptions = 2, books = 13, visitors = 386, newVisitors = 111, loggedInVisitors = 28),
            OfficeAnalyticsBucket("2026-06-25", registrations = 13, payments = 3, creditsViaPayment = 700, codeRedemptions = 3, books = 16, visitors = 550, newVisitors = 159, loggedInVisitors = 40),
            OfficeAnalyticsBucket("2026-06-27", registrations = 5, payments = 1, creditsViaPayment = 250, codeRedemptions = 1, books = 7, visitors = 222, newVisitors = 63, loggedInVisitors = 16),
        )
    }

    fun services(period: String): List<OfficeServiceBucket> {
        val buckets = analytics(period).map { it.date }
        return buckets.mapIndexed { i, date ->
            val n = i + 1
            fun runs(base: Int) = base + (n * 3) % 7
            val r = mapOf(
                "STATIC_ANALYSIS" to runs(9),
                "DEEP_ANALYSIS" to runs(4),
                "ANNOTATION" to runs(2),
                "COVER_BATCH" to runs(1),
                "COVER_REGEN" to (n % 3),
                "TEASER" to (n % 4),
                "AI_HIGHLIGHTS" to runs(0),
            )
            // Teasers fail most often — the slowest, most fragile service.
            val f = mapOf(
                "STATIC_ANALYSIS" to 0,
                "DEEP_ANALYSIS" to (n % 3) / 2,
                "ANNOTATION" to 0,
                "COVER_BATCH" to (n % 5) / 4,
                "COVER_REGEN" to 0,
                "TEASER" to (n % 4) / 2,
                "AI_HIGHLIGHTS" to 0,
            )
            OfficeServiceBucket(
                date = date,
                runs = r,
                done = r.mapValues { (k, v) -> v - (f[k] ?: 0) },
                failed = f,
            )
        }
    }

    fun books() = listOf(
        OfficeBook("user-abc123", "a1b2c3d4", "Мастер и Маргарита.fb2", 148200, true, "2026-06-10T12:00:00Z", true, true, email = "anna@example.com", hasText = true),
        OfficeBook("user-abc123", "e5f6g7h8", "Преступление и наказание.epub", 211500, false, "2026-06-15T09:30:00Z", true, false, email = "anna@example.com", hasText = true),
        OfficeBook("user-xyz789", "i9j0k1l2", "War and Peace.txt", 580000, true, "2026-06-20T17:45:00Z", true, true, email = "leo@example.com", hasText = true),
        OfficeBook("user-qrs456", "m3n4o5p6", "Черновик романа.docx", 42000, false, "2026-06-25T14:00:00Z", true, false, hasText = true),
        OfficeBook("_anon", "q7r8s9t0", "sample_upload.txt", 8500, false, "2026-06-27T08:10:00Z", true, false, hasText = true),
    )

    fun blogList() = listOf(
        BlogSummary(1, "kak-pisat-dialogi", "Как писать диалоги, которые держат читателя", "PUBLISHED", "ru", "grp-1", "Редакция Writers-Lab", 7, 12, 1, 340, 180, "2026-07-11T10:00:00Z", "2026-07-11T10:00:00Z"),
        BlogSummary(3, "how-to-write-dialogue", "How to Write Dialogue That Holds the Reader", "PUBLISHED", "en", "grp-1", "Writers-Lab Team", 7, 4, 0, 120, 64, "2026-07-11T10:30:00Z", "2026-07-11T10:30:00Z"),
        BlogSummary(2, "syuzhetnye-arki", "Сюжетные арки: как не потерять героя к середине", "DRAFT", "ru", "grp-2", "Редакция Writers-Lab", 5, 0, 0, 0, 0, "2026-07-09T14:20:00Z", null),
    )

    fun blogArticle(id: Long) = BlogArticle(
        id = id,
        slug = "kak-pisat-dialogi",
        language = "ru",
        translationKey = "grp-1",
        status = "PUBLISHED",
        title = "Как писать диалоги, которые держат читателя",
        excerpt = "Реплика — это ход в игре, а не обмен информацией. Разбираем, как показать намерение героя.",
        contentMarkdown = "## Диалог начинается до первой реплики\n\nЧитатель верит разговору, когда слышит за словами **намерение** героя.\n\n- кто хочет закончить первым;\n- что каждый скрывает.\n\n> Хороший диалог читается как партия в шахматы.",
        coverImagePath = null,
        coverImageAlt = null,
        metaTitle = null,
        metaDescription = null,
        authorName = "Редакция Writers-Lab",
        readingTimeMinutes = 7,
        likes = 12,
        dislikes = 1,
        createdAt = "2026-07-11T10:00:00Z",
        updatedAt = "2026-07-11T10:00:00Z",
        publishedAt = "2026-07-11T10:00:00Z",
        translations = listOf(TranslationRef(3, "en", "how-to-write-dialogue", "PUBLISHED", "How to Write Dialogue That Holds the Reader")),
    )
}
